import React, { useEffect } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { theme } from '@noths/polaris-client-ribbons-base';
import { TextIcon } from '@noths/polaris-client-ribbons-design-system';
import { CSSProp } from 'styled-components';

import chevronUp from 'src/assets/svg/chevronUp.svg';
import {
  ACCORDION__CONTENT_WRAPPER_CLASS,
  ACCORDION__HEADING_CLASS,
  ACCORDION__TITLE_CLASS,
} from '../constants/classNames';
import * as defaultStyles from '../styles';

export interface AccordionProps {
  title: string;
  extraHeadingContent?: React.ReactNode | null;
  id: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
  renderHeading?: ({ children }: { children: React.ReactChild }) => JSX.Element;
  onToggleOpen?: (updatedIsOpenState: boolean) => void;
  children?: React.ReactNode;
  styles?: CSSProp;
  isOpen?: boolean;
  buttonStyles?: CSSProp;
  titleStyles?: CSSProp;
}

export const Accordion = ({
  id,
  title,
  extraHeadingContent,
  headingLevel = 'h3',
  renderHeading,
  onToggleOpen,
  titleStyles,
  buttonStyles,
  styles,
  children,
  ...props
}: AccordionProps) => {
  const isOpenProp = props.isOpen || false;
  const [isOpen, toggleOpen] = useToggle(isOpenProp);

  const buttonId = `accordion_${id}__button`;
  /*
    Note: changing accordion_copy-block__section could affect tracking
    https://notonthehighstreet.atlassian.net/browse/FFA-514?focusedCommentId=54133)
  */
  const sectionId = `accordion_${id}__section`;
  const Heading = renderHeading || headingLevel;
  const [contentRef, contentDimensions] = useMeasure<HTMLDivElement>();
  const contentHeight = contentDimensions.height;

  const handleToggle = () => {
    toggleOpen();
    onToggleOpen?.(!isOpen);
  };

  useEffect(
    () => {
      if (isOpenProp !== isOpen) {
        handleToggle();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpenProp],
  );

  /*
    Note: removing aria-expanded could affect tracking
    https://notonthehighstreet.atlassian.net/browse/FFA-514?focusedCommentId=54133)
  */
  return (
    <div data-testid="accordion" css={[defaultStyles.accordion, styles]} data-is-open={isOpen}>
      <Heading className={ACCORDION__HEADING_CLASS}>
        <TextIcon
          data-is-open={isOpen}
          css={[defaultStyles.accordionButton, buttonStyles]}
          aria-expanded={isOpen}
          aria-controls={sectionId}
          id={buttonId}
          onClick={handleToggle}
          icon={chevronUp}
          iconWidth={16}
          iconHeight={16}
        >
          {extraHeadingContent ? (
            <div>
              <span
                className={ACCORDION__TITLE_CLASS}
                css={[defaultStyles.accordionTitle, titleStyles]}
              >
                {title}
              </span>
              {extraHeadingContent}
            </div>
          ) : (
            <span
              className={ACCORDION__TITLE_CLASS}
              css={[defaultStyles.accordionTitle, titleStyles]}
            >
              {title}
            </span>
          )}
        </TextIcon>
      </Heading>
      <div
        style={{
          height: isOpen ? (contentHeight ? `${contentHeight + theme.spacing.xl}px` : 'auto') : 0,
        }}
        className={ACCORDION__CONTENT_WRAPPER_CLASS}
        id={sectionId}
        role="region"
        aria-labelledby={buttonId}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};
